import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  Paper,
  Chip,
} from '@mui/material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterSectionTitleSx,
  masterPrimaryBtnSx,
  masterFieldSx,
} from '../../common/MasterPageShell';

const ReceiptSettingsManagement = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ startReceiptNo: '1' });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/receipt-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success' && response.data.data) {
        const data = response.data.data;
        setSettings(data);
        setFormData({ startReceiptNo: data.startReceiptNo || '1' });
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch receipt settings');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value.trim() }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.startReceiptNo || formData.startReceiptNo.trim() === '') {
      setSaveError('Start receipt number cannot be empty');
      return;
    }
    if (!/^\d+$/.test(formData.startReceiptNo.trim())) {
      setSaveError('Start receipt number must be digits only');
      return;
    }
    setSaveLoading(true);
    setSaveError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${baseURL}/receipt-settings`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.data.status === 'success') {
        setSettings(response.data.data);
        setSaveSuccess(true);
      }
      setSaveLoading(false);
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to save receipt settings');
      setSaveLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSaveSuccess(false);
    setSaveError(null);
  };

  return (
    <MasterPageShell title="Receipt Settings" subtitle="Master · Numbering" loading={loading}>
      {error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={masterPaperSx}>
              <Typography sx={masterSectionTitleSx}>Start receipt number</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Next receipts continue sequentially from this number.
              </Typography>
              <Box component="form" onSubmit={handleSave} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="startReceiptNo"
                  label="Start receipt number"
                  name="startReceiptNo"
                  value={formData.startReceiptNo}
                  onChange={handleInputChange}
                  sx={masterFieldSx}
                />
                <Button type="submit" fullWidth variant="contained" sx={masterPrimaryBtnSx} disabled={saveLoading}>
                  {saveLoading ? 'Saving…' : 'Save settings'}
                </Button>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={masterPaperSx}>
              <Typography sx={masterSectionTitleSx}>Current</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={`Start: ${settings?.startReceiptNo || formData.startReceiptNo || '—'}`}
                  sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar open={saveSuccess} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%', borderRadius: 2 }}>
          Receipt settings saved
        </Alert>
      </Snackbar>
      <Snackbar open={!!saveError} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%', borderRadius: 2 }}>
          {saveError}
        </Alert>
      </Snackbar>
    </MasterPageShell>
  );
};

export default ReceiptSettingsManagement;
